import type { PendingRow, RowValues } from '@w/db-replication/pending-rows'
import type { Tel } from '@w/tel'
import { AUTH_ANONYMOUS_KEY } from '@w/auth-fe'
import { hasUserContent } from '../db/reexpress-pending'
import { replicatedTables } from '../db/workspace-tables'

/**
 * flashcards-local-first.spec.md AttachBinding: the durable record that source anonymous user X's data belongs to
 * account A. Written before any staging, under `["local-first-attach", X]`, and kept until X is retired. Its target
 * never changes (decisions "Attach: … durable binding"; lead decision 6: a source bound to A never goes to B).
 */
export type AttachBinding = { source: string; target: string; state: 'staging' | 'committing' | 'awaiting-outcomes' }

const bindingKey = (source: string) => JSON.stringify(['local-first-attach', source])

/** Every binding on this device (a device can hold several: X bound to A, then Y bound to B). */
export function readBindings(storage: Storage): AttachBinding[] {
  const bindings: AttachBinding[] = []
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index)
    if (key?.startsWith('["local-first-attach",')) bindings.push(JSON.parse(storage.getItem(key) ?? 'null') as AttachBinding)
  }
  return bindings
}

export function writeBinding(storage: Storage, binding: AttachBinding) {
  storage.setItem(bindingKey(binding.source), JSON.stringify(binding))
}

export function deleteBinding(storage: Storage, source: string) {
  storage.removeItem(bindingKey(source))
}

/**
 * Q4 A (the first account that signs in gets the anonymous work): when account `target` is current and the device
 * still holds an anonymous user X with no binding, X is bound to `target`, then stops being the device's anonymous
 * user (state `bound`: X is no longer current), so a later anonymous session is a fresh user, never X.
 */
export function bindCurrentAnonymous(storage: Storage, target: string): AttachBinding | undefined {
  const stored = storage.getItem(AUTH_ANONYMOUS_KEY)
  if (!stored) return undefined
  const source = (JSON.parse(stored) as { id: string }).id
  const existing = readBindings(storage).find(binding => binding.source === source)
  const binding = existing ?? { source, target, state: 'staging' as const }
  if (!existing) writeBinding(storage, binding)
  storage.removeItem(AUTH_ANONYMOUS_KEY)
  return binding
}

type Query = <T>(sql: string, params?: unknown[]) => Promise<T[]>

/**
 * Attach step 2: X's content pages and every ancestor up to X's personal root (empty structural parents included),
 * parents first, and the child edges that place them, with X's personal root remapped to A's; then X's learner rows,
 * each card before its events (decisions "Attach": learner mapping, (X, qid) → (A, qid), schedule columns and event
 * ids, data and stamps copied). X's personal root and its grant are never staged. Each row is work A's server lacks
 * (base null), so re-express writes it whole as A, after the pages (the card's question comes from its page).
 */
export async function stageAnonymousWork(query: Query, binding: Pick<AttachBinding, 'source' | 'target'>, targetRoot: string): Promise<PendingRow[]> {
  // X's workspace has one member, so at most one personal root (kind `personalRoot`, db-crdt-sync.spec.md root-pages-have-ulid-ids).
  const sourceRoot = (await query<{ id: string }>("SELECT id FROM pages WHERE kind='personalRoot'"))[0]?.id
  const layout = (name: string) => {
    const table = replicatedTables.find(candidate => candidate.name === name)
    if (!table) throw new Error(`INV: ${name} is a replicated table`)
    return table
  }
  const pageTable = layout('pages')
  const edgeTable = layout('pageRelations')
  const cardTable = layout('flashcards')
  const eventTable = layout('flashcardEvents')
  const select = (table: ReturnType<typeof layout>) => table.columns.map(column => `"${column.name}"`).join(',')
  const pages = await query<RowValues>(`SELECT ${select(pageTable)} FROM pages WHERE kind<>'personalRoot'`)
  const edges = await query<RowValues>(`SELECT ${select(edgeTable)} FROM pageRelations WHERE type='child'`)
  const parentOf = new Map(edges.map(edge => [String(edge.toPageId), String(edge.fromPageId)]))
  const byId = new Map(pages.map(page => [String(page.id), page]))
  const staged = new Set<string>()
  for (const page of pages) {
    if (!hasUserContent({ title: page.title as string | null, data: page.data as Uint8Array })) continue
    for (let id: string | undefined = String(page.id); id !== undefined && id !== sourceRoot && byId.has(id) && !staged.has(id); id = parentOf.get(id)) staged.add(id)
  }
  const depth = (id: string): number => { const parent = parentOf.get(id); return parent === undefined || parent === sourceRoot || !staged.has(parent) ? 0 : 1 + depth(parent) }
  const ordered = [...staged].toSorted((left, right) => depth(left) - depth(right))
  const row = (table: ReturnType<typeof layout>, local: RowValues): PendingRow =>
    ({ table: table.name, key: Object.fromEntries(table.primaryKey.map(name => [name, local[name] ?? null])), base: null, local })
  const rows = ordered.flatMap(id => {
    const edge = edges.find(candidate => String(candidate.toPageId) === id)
    const placed = [row(pageTable, safePage(byId.get(id)))]
    if (edge) placed.push(row(edgeTable, Object.assign({}, edge, { fromPageId: edge.fromPageId === sourceRoot ? targetRoot : edge.fromPageId })))
    return placed
  })
  const learnerRows = async (table: ReturnType<typeof layout>, order: string) => {
    for (const local of await query<RowValues>(`SELECT ${select(table)} FROM "${table.name}" WHERE userId=? ORDER BY ${order}`, [binding.source]))
      rows.push(row(table, Object.assign({}, local, { userId: binding.target })))
  }
  await learnerRows(cardTable, 'qid')
  await learnerRows(eventTable, 'occurredAt, id')
  return rows
}

function safePage(page: RowValues | undefined): RowValues {
  if (!page) throw new Error('INV: a staged page was read')
  return page
}

export type AttachDevice = {
  storage: Storage
  tel: Tel
  /** Opens X's replica file on this device, offline (the anonymous principal's local backend). */
  openSource: (source: string) => Promise<{ query: Query; close(): Promise<void> }>
  /** Holds the carried rows in A's personal workspace, calls `held` once they are durable there, then re-expresses
   * them as A (spaces-resources importCarriedRows). */
  commit: (target: string, rows: readonly PendingRow[], held: () => void) => Promise<void>
  /** A's personal root in its personal workspace `target`, from A's directory entry. */
  personalRootId: (target: string) => string
}

/**
 * flashcards-local-first.spec.md Attach, steps 2-3: carry X's pages into A's personal workspace. The binding moves to
 * `awaiting-outcomes` as soon as the rows are held in A's file (before they are re-expressed), so a restart never
 * stages X a second time on top of a commit (a second copy of X's history would merge into A's fresh documents as
 * duplicate blocks); held rows re-express on A's next open by themselves. Until then (`staging`, or `committing` after
 * a crash or a throw before the rows were held) every sign-in of A stages again: held rows upsert by key.
 */
export async function attachBound(device: AttachDevice, binding: AttachBinding) {
  if (binding.state === 'awaiting-outcomes') return
  const source = await device.openSource(binding.source)
  let rows: PendingRow[]
  try { rows = await stageAnonymousWork(source.query, binding, device.personalRootId(binding.target)) } finally { await source.close() }
  writeBinding(device.storage, { ...binding, state: 'committing' })
  await device.commit(binding.target, rows, () => { writeBinding(device.storage, { ...binding, state: 'awaiting-outcomes' }) })
}
